import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid";
import { Box, Button, InputAdornment, TextField, Typography, Paper, Stack, Divider } from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import { IBossSalary } from "../../../interface/bossSalary";
import getBossSalary from "../../../tools/forecast/getBossSalary";
import { useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/reducers";
import { updateBossSalary } from "../../../tools/forecast/updateBossSalary";
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SaveIcon from '@mui/icons-material/Save';

const AnnualForecastTotalData = ({ forecastVersion, forecast, totalBySection }: { forecastVersion?: number, forecast?: string, totalBySection: any }) => {
    const dispatch = useDispatch();
    const token = useSelector((state: RootState) => state.user.token) as string;
    const params = useParams();

    const [bossSalaryAmount, setBossSalaryAmount] = useState(0);
    const [bossSalary, setBossSalary] = useState<IBossSalary>();
    const [error, setError] = useState('');

    useEffect(() => {
        getBossSalary(params.id, forecastVersion, setBossSalary, token, dispatch, setError);
    }, [forecastVersion, params.id, token, dispatch]);

    useEffect(() => {
        if (bossSalary) setBossSalaryAmount(bossSalary.amount);
    }, [bossSalary]);

    const handleUpdate = () => {
        updateBossSalary(token, bossSalaryAmount, dispatch, setError, params.id, forecastVersion);
    };

    const handleBossSalary = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(event.target.value) || 0;
        setBossSalaryAmount(parseFloat(value.toFixed(2)));
    };

    // --- LOGIQUE DE CALCUL ---
    const { totalCA, totalDepenses } = useMemo(() => {
        const caKeys = ["CA - Régie", "CA - Forfait", "CA - Hébergement", "CA - TMA/TME", "CA - TRA"];
        const depKeys = ["Salariés", "Epargne", "TNS", "Investissements", "Frais de fonctionnement", "Sous-traitance", "DGFIP", "Entreprise"];

        // On précise (acc: number, key: string)
        const ca = caKeys.reduce((acc: number, key: string) => {
            const values = totalBySection[key] ? Object.values(totalBySection[key]) : [];
            // On précise aussi (sum: number, val: any)
            return acc + values.reduce((sum: number, val: any) => sum + (Number(val) || 0), 0);
        }, 0);

        const dep = depKeys.reduce((acc: number, key: string) => {
            const values = totalBySection[key] ? Object.values(totalBySection[key]) : [];
            return acc + values.reduce((sum: number, val: any) => sum + (Number(val) || 0), 0);
        }, 0);

        return { totalCA: ca, totalDepenses: dep };
    }, [totalBySection]);

    const beforeISAmount = totalCA - totalDepenses - (bossSalaryAmount * 1.5 || 0);
    const amountIS = totalCA > 42500 ? (42500 * 0.15 + (totalCA - 42500) * 0.25) : totalCA * 0.15;

    // --- COLONNES ---
    const columns: GridColDef[] = [
        {
            field: "field",
            headerName: "Indicateur",
            minWidth: 400,
            flex: 1,
            renderCell: (params: any) => (
                <Typography variant="body1" sx={{ fontWeight: params.row.isTotal ? 800 : 500 }}>
                    {params.value}
                </Typography>
            )
        },
        {
            field: "forecastData",
            headerName: forecast || "Résultat Annuel",
            minWidth: 400,
            align: "right",
            headerAlign: 'right',
            renderCell: (params: any) => {
                if (params.row.id === 'bossSalary') {
                    return (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, justifyContent: "flex-end" }}>
                            <TextField
                                size="small"
                                type="number"
                                value={bossSalaryAmount}
                                onChange={handleBossSalary}
                                InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                                sx={{ width: 140 }}
                            />
                            <Button size="small" variant="contained" onClick={handleUpdate} startIcon={<SaveIcon />}>
                                Maj
                            </Button>
                        </Box>
                    );
                }
                return (
                    <Typography 
                        variant="body1" 
                        sx={{ 
                            fontWeight: params.row.isTotal ? 800 : 600,
                            color: params.row.color || "inherit"
                        }}
                    >
                        {params.value}
                    </Typography>
                );
            },
        }
    ];

    // --- LIGNES ---
    const rows: GridRowsProp = [
        { id: "CA", field: "Chiffre d'Affaires Global", forecastData: `${totalCA.toLocaleString()} €`, color: "#059669" },
        { id: "dépenses", field: "Total des Dépenses", forecastData: `- ${totalDepenses.toLocaleString()} €`, color: "#dc2626" },
        { id: "beforeBossSalary", field: "Résultat avant rémunération", forecastData: `${(totalCA - totalDepenses).toLocaleString()} €`, isTotal: true },
        { id: "bossSalary", field: "Rémunération du gérant", forecastData: "" },
        { id: "socialCharges", field: "Charges sociales (Est.)", forecastData: `- ${(bossSalaryAmount / 2 || 0).toLocaleString()} €`, color: "#64748b" },
        { id: "beforeIS", field: "Bénéfice avant IS", forecastData: `${beforeISAmount.toLocaleString()} €`, isTotal: true },
        { id: "IS", field: "Impôt sur la société (IS)", forecastData: `- ${amountIS.toLocaleString()} €`, color: "#dc2626" },
        { id: "profits", field: "BÉNÉFICE NET FINAL", forecastData: `${(beforeISAmount - amountIS).toLocaleString()} €`, isTotal: true, color: "#2563eb" }
    ];

    return (
        <Box sx={{ mt: 4, mb: 6, display: 'flex', justifyContent: 'center' }}>
            <Paper sx={{ 
                p: 4, 
                borderRadius: 4, 
                width: '100%', 
                maxWidth: 1100, 
                border: "1px solid #e2e8f0", 
                boxShadow: "0px 10px 30px rgba(0,0,0,0.05)" 
            }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <AccountBalanceWalletIcon sx={{ color: "#f59e0b", fontSize: 30 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#1e293b" }}>
                        Synthèse Annuelle Prévisionnelle
                    </Typography>
                </Stack>
                
                <Divider sx={{ mb: 3 }} />

                <DataGrid
                    rows={rows}
                    columns={columns}
                    autoHeight
                    hideFooter
                    disableRowSelectionOnClick
                    sx={{
                        border: "none",
                        "& .MuiDataGrid-columnHeaders": { display: "none" }, // On cache pour un look plus "Stats"
                        "& .MuiDataGrid-cell": { borderBottom: "1px solid #f1f5f9", py: 2 },
                        "& .MuiDataGrid-row:hover": { bgcolor: "#f8fafc" }
                    }}
                />
            </Paper>
        </Box>
    );
};

export default AnnualForecastTotalData;